import React, { useRef } from "react";
import {
  Chart,
  Series,
  ArgumentAxis,
  ValueAxis,
  Legend,
  Tooltip,
  CommonSeriesSettings,
  Label,
  Grid,
  Point,
} from "devextreme-react/chart";
import ChartCard from "components/common/pyramid/ChartCard";

const AmortizationGapBySecurity = ({ data, palette, title }) => {
  const chartRef = useRef(null);

  const customizeTooltip = (info) => {
    const isin = info.point?.data?.isin ? info.point.data.isin : "";

    const titleHtml = `
      <div style="font-weight: bold; margin-bottom: 2px;">
        ${info.argumentText}
      </div>
      ${
        isin
          ? `<div style="font-size: 12px; font-weight: bold; margin-bottom: 7px;">${isin}</div>`
          : ""
      }
    `;
    return {
      html: `
        <div style="padding: 10px;">
          ${titleHtml}
          <div>Amortization gap: ${new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          }).format(info.point.data.amortizationGapTotal)}</div>
          <div>Cumulative percentage gap: ${formatPercentage(
            info.point.data.cumPctGap / 100
          )}</div>
        </div>
      `,
    };
  };

  const formatPercentage = (value) => {
    const percentValue = value * 100;
    if (percentValue === Math.floor(percentValue)) {
      return `${Math.floor(percentValue)}%`;
    } else if (Math.round(percentValue) === 100) {
      return "100%";
    }
    return new Intl.NumberFormat("en-US", {
      style: "percent",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(value);
  };

  return (
    <ChartCard
      title={title || "Amortization Gap by Security"}
      chartRef={chartRef}
      palette={palette}
    >
      <div className="p-4">
        <Chart
          dataSource={data}
          id="securityChart"
          ref={chartRef}
          palette={palette}
        >
          <CommonSeriesSettings argumentField="securityLib" />

          {/* Bar series (Total Amortization Gap) */}
          <Series
            name="Amortization Gap"
            valueField="amortizationGapTotal"
            axis="left"
            type="bar"
          ></Series>

          {/* Line series (Cumulative percentage) */}
          <Series
            name="Cumulative percentage gap"
            valueField="cumPctGap"
            axis="right"
            type="spline"
          >
            <Point visible={true} size={7} symbol="circle" />
          </Series>

          <ArgumentAxis>
            <Label
              wordWrap="normal"
              textOverflow="none"
              overlappingBehavior="stagger"
            />
          </ArgumentAxis>

          {/* Left axis (Total Amortization Gap) */}
          <ValueAxis
            name="left"
            position="left"
            showZero={true}
            tickInterval={10}
          >
            <Label
              customizeText={(e) => {
                return new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "EUR",
                  notation: "compact",
                  compactDisplay: "short",
                }).format(e.value);
              }}
            />
            <Grid visible={true} />
          </ValueAxis>

          {/* Right axis (Cumulative percentage) */}
          <ValueAxis name="right" position="right" tickInterval={10}>
            <Label
              customizeText={(e) => {
                if (e.value === Math.floor(e.value)) {
                  return `${Math.floor(e.value)}%`;
                }
                return `${e.value.toFixed(1)}%`;
              }}
            />
          </ValueAxis>

          <Legend
            verticalAlignment="top"
            horizontalAlignment="center"
            itemTextPosition="right"
          />

          <Tooltip
            enabled={true}
            customizeTooltip={customizeTooltip}
            shared={true}
          />
        </Chart>
      </div>
    </ChartCard>
  );
};

export default AmortizationGapBySecurity;
